// Wise-styled modal for asking about a product (based on AskProductModal)

import React, { type FC, useMemo, useState } from 'react';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import { usePostProductAskQuestion } from 'api';
import type { IProduct } from 'api/types';
import { useNotifications } from 'hooks';
import { useSelector } from 'store';

import WiseModal from 'components/wise/WiseModal';

import styles from 'theme/components/wise/WiseAskProductModal/WiseAskProductModal.module.scss';

export type WiseAskProductModalProps = {
  product: IProduct;
  open: boolean;
  onClose: () => void;
};

const WiseAskProductModal: FC<WiseAskProductModalProps> = ({ product, open, onClose }) => {
  const { t } = useTranslation();
  const { overloginUserId } = useSelector((state) => state.auth);
  const { showSuccessMessage, showErrorMessage } = useNotifications();

  const isOpenProfile = !overloginUserId;

  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ message?: string; email?: string; phone?: string }>({});

  const { mutate: sendQuestion, isLoading } = usePostProductAskQuestion(product.id, {
    onSuccess: () => {
      showSuccessMessage(t('Zapytanie zostało wysłane'));
      handleClose();
    },
    onError: (error: any) => {
      showErrorMessage(error?.response?.data?.message || t('Wystąpił błąd podczas wysyłania zapytania'));
    }
  });

  const handleClose = () => {
    setMessage('');
    setEmail('');
    setPhone('');
    setErrors({});
    onClose();
  };

  const validateEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue.trim());
  };

  const validatePhone = (phoneValue: string): boolean => {
    const phoneRegex = /^\+?[1-9][0-9]{7,14}$/;
    return phoneRegex.test(phoneValue.trim().replace(/\s/g, ''));
  };

  const validate = (): boolean => {
    const newErrors: { message?: string; email?: string; phone?: string } = {};

    if (!message.trim()) {
      newErrors.message = t('Treść zapytania jest wymagana');
    }

    if (isOpenProfile) {
      const emailTrimmed = email.trim();
      const phoneTrimmed = phone.trim();

      if (!emailTrimmed && !phoneTrimmed) {
        newErrors.email = t('Musi być podany adres e-mail lub numer telefonu');
        newErrors.phone = t('Musi być podany adres e-mail lub numer telefonu');
      } else {
        if (emailTrimmed && !validateEmail(emailTrimmed)) {
          newErrors.email = t('Niepoprawny adres email');
        }
        if (phoneTrimmed && !validatePhone(phoneTrimmed)) {
          newErrors.phone = t('Niepoprawny numer telefonu');
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    sendQuestion({
      message: message.trim(),
      ...(isOpenProfile && email.trim() ? { email: email.trim() } : {}),
      ...(isOpenProfile && phone.trim() ? { phone: phone.trim() } : {})
    });
  };

  const productName = useMemo(
    () => product?.product_basic_short_info?.title || product?.product_basic_full_info?.title || '',
    [product]
  );
  const productIndex = useMemo(
    () => product?.product_basic_short_info?.index || product?.product_basic_full_info?.index || '',
    [product]
  );

  const modalTitle = productName ? `${t('Zapytaj o produkt:')} ${productName}` : t('Zapytaj o produkt:');

  return (
    <WiseModal open={open} onClose={handleClose} title={modalTitle} maxWidth={860}>
      <div
        className={classnames(
          styles.root,
          'StylePath-Components-Wise-WiseAskProductModal'
        )}>
        {productIndex && (
          <div className={styles.productIndex}>
            <Trans>INDEX</Trans>: {productIndex}
          </div>
        )}

        <div className={styles.field}>
          <TextField
            className={styles.textField}
            label={t('Treść zapytania')}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (errors.message) {
                setErrors({ ...errors, message: undefined });
              }
            }}
            placeholder={t('Wpisz swoje zapytanie')}
            multiline
            minRows={6}
            fullWidth
            error={!!errors.message}
            helperText={errors.message}
          />
        </div>

        {isOpenProfile && (
          <div className={styles.grid2}>
            <div className={styles.field}>
              <TextField
                className={styles.textField}
                label={t('Adres e-mail')}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) {
                    setErrors({ ...errors, email: undefined });
                  }
                }}
                placeholder={t('test@example.com')}
                autoComplete="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
              />
            </div>

            <div className={styles.field}>
              <TextField
                className={styles.textField}
                label={t('Numer telefonu')}
                type="tel"
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (errors.phone) {
                    setErrors({ ...errors, phone: undefined });
                  }
                }}
                placeholder={t('+48123456789')}
                autoComplete="tel"
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <Button
            variant="contained"
            className={classnames(styles.button, styles.buttonGhost)}
            onClick={handleClose}
            disabled={isLoading}>
            <Trans>Anuluj</Trans>
          </Button>

          <Button
            variant="contained"
            className={classnames(styles.button, styles.buttonPrimary)}
            onClick={handleSubmit}
            disabled={isLoading}>
            {isLoading ? t('Wysyłanie…') : t('Wyślij zapytanie')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default WiseAskProductModal;

